import React, { useEffect, useState } from 'react';
import projectAPI from '../api/projectAPI';
import UserDashboard from './UserDashboard';
import {
  FaChevronRight, FaLink, FaTimes, FaDownload,
  FaEye, FaFilePdf, FaFileWord, FaFileImage, FaFile,
} from 'react-icons/fa';

// ── Helpers ───────────────────────────────────────────────────────────────────
const ROLE_COLOR = {
  'Marketing':     'bg-pink-100 text-pink-700',
  'Project Leads': 'bg-indigo-100 text-indigo-700',
  'HR & Design':   'bg-teal-100 text-teal-700',
  'TL':            'bg-blue-100 text-blue-700',
  'Developer':     'bg-green-100 text-green-700',
  'Office':        'bg-gray-100 text-gray-600',
  'admin':         'bg-purple-100 text-purple-700',
};
const RoleBadge = ({ role }) => (
  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ROLE_COLOR[role] || 'bg-gray-100 text-gray-600'}`}>
    {role}
  </span>
);

const StatusBadge = ({ status }) => {
  const s =
    status === 'Completed' ? 'bg-green-100 text-green-700' :
    status === 'On Hold'   ? 'bg-blue-100 text-blue-700'   :
                             'bg-yellow-100 text-yellow-700';
  return <span className={`text-xs font-semibold px-2 py-1 rounded-full ${s}`}>{status}</span>;
};

const ChainTrail = ({ chain }) => {
  if (!chain?.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {chain.map((entry, i) => (
        <React.Fragment key={i}>
          <span className="text-xs bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 rounded-full">
            {entry}
          </span>
          {i < chain.length - 1 && <FaChevronRight className="text-purple-300 text-xs" />}
        </React.Fragment>
      ))}
    </div>
  );
};

const FileIcon = ({ fileType }) => {
  if (fileType === 'image') return <FaFileImage className="text-blue-500 text-lg shrink-0" />;
  if (fileType === 'pdf')   return <FaFilePdf   className="text-red-500  text-lg shrink-0" />;
  if (fileType === 'doc')   return <FaFileWord  className="text-blue-700 text-lg shrink-0" />;
  return <FaFile className="text-gray-500 text-lg shrink-0" />;
};

const fmtDate = (d) => {
  if (!d) return '—';
  const parsed = new Date(d);
  return isNaN(parsed) ? '—' : parsed.toLocaleDateString('en-GB');
};

// ─────────────────────────────────────────────────────────────────────────────
const WorkerProjectTracking = () => {
  const worker   = JSON.parse(localStorage.getItem('worker') || 'null');
  const workerId = worker?._id;

  const [projects, setProjects]         = useState([]);
  const [searchTerm, setSearchTerm]     = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [showDetail, setShowDetail]     = useState(false);
  const [detailProject, setDetailProject] = useState(null);
  const [previewUrl, setPreviewUrl]     = useState(null);

  useEffect(() => { fetchProjects(); }, []);

  // ── Fetch ALL company projects — every worker sees everything ─────────────
  const fetchProjects = async () => {
    try {
      const res    = await projectAPI.get('/');
      const sorted = res.data.sort(
        (a, b) => new Date(b.createdAt || b._id) - new Date(a.createdAt || a._id)
      );
      setProjects(sorted);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  };

  const openDetail = (project) => { setDetailProject(project); setShowDetail(true); };

  const filtered = projects.filter((p) => {
    const matchSearch = p.title?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'All' || p.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-pink-100 p-6 pt-24">
      <UserDashboard />

      <div className="max-w-7xl mx-auto bg-white p-6 rounded-xl shadow-lg mt-5">

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-5 gap-4 flex-wrap">
          <div>
            <h2 className="text-3xl font-bold font-mono text-purple-700">Project Tracking</h2>
            {worker && (
              <p className="text-sm text-gray-500 mt-0.5">
                {worker.username} <RoleBadge role={worker.role} />
                <span className="ml-2 text-xs text-gray-400">· {filtered.length} project(s)</span>
              </p>
            )}
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            {['All', 'Ongoing', 'On Hold', 'Completed'].map((s) => (
              <button key={s} onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition ${
                  statusFilter === s
                    ? 'bg-purple-600 text-white border-purple-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-purple-400'
                }`}>{s}</button>
            ))}
            <input
              type="text"
              placeholder="Search by title…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border px-4 py-2 rounded-lg w-full md:w-64 text-sm"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto max-h-[500px] overflow-auto">
          <table className="min-w-full table-auto text-sm">
            <thead className="bg-purple-100 text-purple-700 sticky top-0 z-10">
              <tr>
                <th className="py-3 px-4 text-left">Start</th>
                <th className="py-3 px-4 text-left">End</th>
                <th className="py-3 px-4 text-left">Title</th>
                <th className="py-3 px-4 text-left">Created By</th>
                <th className="py-3 px-4 text-left">Assigned To</th>
                <th className="py-3 px-4 text-left">Chain</th>
                <th className="py-3 px-4 text-left">Status</th>
                <th className="py-3 px-4 text-left">Files</th>
                <th className="py-3 px-4 text-left">Reports</th>
                <th className="py-3 px-4 text-left">Detail</th>
              </tr>
            </thead>
            <tbody className="text-gray-700">
              {filtered.map((project) => (
                <tr key={project._id} className="border-t hover:bg-purple-50 transition">

                  <td className="py-2 px-4 whitespace-nowrap text-xs">{fmtDate(project.startDate)}</td>
                  <td className="py-2 px-4 whitespace-nowrap text-xs">{fmtDate(project.endDate)}</td>

                  <td className="py-2 px-4">
                    <p className="font-semibold text-gray-800">{project.title}</p>
                    {project.description && (
                      <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{project.description}</p>
                    )}
                  </td>

                  <td className="py-2 px-4">
                    {project.createdBy?.username ? (
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className="text-xs font-medium text-gray-700">{project.createdBy.username}</span>
                        <RoleBadge role={project.createdBy.role} />
                      </div>
                    ) : <span className="text-xs text-gray-400">—</span>}
                  </td>

                  <td className="py-2 px-4">
                    {project.assignees?.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {project.assignees.slice(0, 2).map((a, i) => (
                          <span key={i} className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${
                            a.userId?.toString() === workerId
                              ? 'bg-purple-100 text-purple-700 border border-purple-300'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {a.username}
                            {a.userId?.toString() === workerId && <span className="text-[10px]">★</span>}
                          </span>
                        ))}
                        {project.assignees.length > 2 && (
                          <span className="text-xs text-gray-400">+{project.assignees.length - 2}</span>
                        )}
                      </div>
                    ) : <span className="text-xs text-gray-400">—</span>}
                  </td>

                  <td className="py-2 px-4">
                    {project.assignmentChain?.length > 0
                      ? <ChainTrail chain={project.assignmentChain} />
                      : <span className="text-xs text-gray-400">—</span>}
                  </td>

                  {/* Status — read-only, only admin can change */}
                  <td className="py-2 px-4">
                    <StatusBadge status={project.status} />
                  </td>

                  <td className="py-2 px-4 text-xs text-blue-600">
                    {project.attachments?.length > 0 ? `${project.attachments.length} file(s)` : '—'}
                  </td>

                  <td className="py-2 px-4 text-xs text-indigo-600">
                    {project.reports?.length > 0 ? `${project.reports.length} report(s)` : '—'}
                  </td>

                  <td className="py-2 px-4">
                    <button onClick={() => openDetail(project)}
                      className="flex items-center gap-1 text-xs bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-lg transition">
                      <FaLink /> View
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan="10" className="text-center py-8 text-gray-400">No projects found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── DETAIL MODAL ─────────────────────────────────────────────────────── */}
      {showDetail && detailProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white w-full max-w-2xl rounded-xl shadow-2xl p-6 relative max-h-[90vh] overflow-y-auto">
            <button onClick={() => setShowDetail(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-xl"><FaTimes /></button>

            <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
              <div>
                <h3 className="text-xl font-bold text-purple-700">{detailProject.title}</h3>
                <p className="text-sm text-gray-500 mt-1">{detailProject.description || '—'}</p>
              </div>
              <StatusBadge status={detailProject.status} />
            </div>

            <div className="flex gap-6 text-xs text-gray-500 mb-4">
              <span>Start: <span className="font-medium text-gray-700">{fmtDate(detailProject.startDate)}</span></span>
              <span>End: <span className="font-medium text-gray-700">{fmtDate(detailProject.endDate)}</span></span>
            </div>

            {detailProject.createdBy?.username && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-400 mb-1">Created by</p>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-700">{detailProject.createdBy.username}</span>
                  <RoleBadge role={detailProject.createdBy.role} />
                </div>
              </div>
            )}

            {detailProject.assignmentChain?.length > 0 && (
              <div className="mb-4 p-3 bg-purple-50 rounded-lg">
                <p className="text-xs text-gray-400 mb-2">Assignment chain</p>
                <ChainTrail chain={detailProject.assignmentChain} />
              </div>
            )}

            {detailProject.assignees?.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-gray-400 mb-2">All people on this project</p>
                <div className="space-y-2">
                  {detailProject.assignees.map((a, i) => (
                    <div key={i} className={`flex items-center gap-2 p-2 rounded-lg border ${
                      a.userId?.toString() === workerId
                        ? 'bg-purple-50 border-purple-300'
                        : 'bg-gray-50 border-gray-200'
                    }`}>
                      <span className="text-sm font-medium text-gray-700 flex-1">{a.username}</span>
                      <RoleBadge role={a.role} />
                      <span className="text-xs text-gray-400">← {a.assignedBy}</span>
                      {a.userId?.toString() === workerId && (
                        <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full">You</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {detailProject.attachments?.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-gray-400 mb-2 uppercase font-medium tracking-wide">
                  Attachments ({detailProject.attachments.length})
                </p>
                <div className="space-y-2">
                  {detailProject.attachments.map((f) => (
                    <div key={f._id} className="flex items-center gap-3 bg-gray-50 border rounded-lg px-3 py-2">
                      <FileIcon fileType={f.fileType} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700 truncate">{f.name}</p>
                        <p className="text-xs text-gray-400">
                          by {f.uploadedBy} · {new Date(f.uploadedAt).toLocaleDateString('en-GB')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {f.fileType === 'image' && (
                          <button onClick={() => setPreviewUrl(f.url)}
                            className="text-blue-500 hover:text-blue-700"><FaEye /></button>
                        )}
                        <a href={f.url} target="_blank" rel="noopener noreferrer"
                          className="text-green-500 hover:text-green-700"><FaDownload /></a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {detailProject.reports?.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-gray-400 mb-2 uppercase font-medium tracking-wide">
                  Reports ({detailProject.reports.length})
                </p>
                <div className="space-y-3">
                  {detailProject.reports.map((r, i) => (
                    <div key={i} className="border rounded-xl p-3 bg-indigo-50">
                      <div className="flex justify-between items-start flex-wrap gap-1 mb-2">
                        <span className="text-sm font-semibold text-indigo-700">{r.submittedBy}</span>
                        <span className="text-xs text-gray-400">
                          {new Date(r.submittedAt).toLocaleString('en-GB')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 mb-2">{r.description}</p>
                      {r.photo && (
                        <img src={r.photo} alt="report"
                          className="w-full rounded-lg border cursor-pointer hover:opacity-90"
                          onClick={() => setPreviewUrl(r.photo)} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!detailProject.attachments?.length && !detailProject.reports?.length && (
              <p className="text-sm text-gray-400 text-center py-4">No attachments or reports yet.</p>
            )}
          </div>
        </div>
      )}

      {/* ── IMAGE PREVIEW ─────────────────────────────────────────────────────── */}
      {previewUrl && (
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-80 flex items-center justify-center px-4"
          onClick={() => setPreviewUrl(null)}>
          <div className="relative max-w-3xl w-full">
            <button onClick={() => setPreviewUrl(null)}
              className="absolute -top-10 right-0 text-white text-2xl hover:text-red-400">✕</button>
            <img src={previewUrl} alt="preview" className="w-full rounded-xl shadow-2xl" />
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkerProjectTracking;